import { useMemo, useState, useCallback, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi, type AdminRole, type CompanyRoleStats } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable, Button } from "@/components/ui";
import type { ColumnDef, RowAction } from "@/components/ui";
import {
  Trash2,
  RotateCcw,
  Edit,
  Plus,
  KeyRound,
  Users,
  Building2,
  Shield,
  Check,
  X,
} from "lucide-react";
import { AdminRoleModal } from "./AdminRoleModal";
import {
  MEMBERSHIP_ROLES,
  ROLE_INFO,
  ROLE_HIERARCHY,
  type MembershipRole,
} from "@kvanten/shared";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

// ═══════════════════════════════════════════════════════════════════
// Types & Constants
// ═══════════════════════════════════════════════════════════════════

type TabId = "admin-roles" | "company-roles";

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: "admin-roles", label: "Admin-roller", icon: Shield },
  { id: "company-roles", label: "Företagsroller", icon: Building2 },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Aktiva" },
  { value: "all", label: "Alla" },
];

// Company role permissions matrix
const COMPANY_PERMISSIONS = [
  { id: "company:view", label: "Visa företagsinfo", category: "Företag" },
  { id: "company:update", label: "Uppdatera företagsinfo", category: "Företag" },
  { id: "company:delete", label: "Radera företaget", category: "Företag" },
  { id: "members:view", label: "Visa medlemmar", category: "Medlemmar" },
  { id: "members:invite", label: "Bjuda in medlemmar", category: "Medlemmar" },
  { id: "members:remove", label: "Ta bort medlemmar", category: "Medlemmar" },
  { id: "members:change_role", label: "Ändra roller", category: "Medlemmar" },
  { id: "reports:view", label: "Visa årsredovisningar", category: "Rapporter" },
  { id: "reports:create", label: "Skapa årsredovisning", category: "Rapporter" },
  { id: "reports:edit", label: "Redigera årsredovisning", category: "Rapporter" },
  { id: "reports:delete", label: "Radera årsredovisning", category: "Rapporter" },
  { id: "reports:export", label: "Exportera PDF/XML", category: "Rapporter" },
  { id: "billing:view", label: "Visa betalningshistorik", category: "Betalning" },
  { id: "billing:manage", label: "Hantera betalning", category: "Betalning" },
  { id: "board:view", label: "Visa styrelse", category: "Styrelse" },
  { id: "board:edit", label: "Redigera styrelse", category: "Styrelse" },
] as const;

// Which permissions each role has
const ROLE_PERMISSIONS: Record<MembershipRole, string[]> = {
  owner: [
    "company:view", "company:update", "company:delete",
    "members:view", "members:invite", "members:remove", "members:change_role",
    "reports:view", "reports:create", "reports:edit", "reports:delete", "reports:export",
    "billing:view", "billing:manage",
    "board:view", "board:edit",
  ],
  manager: [
    "company:view",
    "members:view", "members:invite", "members:remove", "members:change_role",
    "reports:view", "reports:create", "reports:edit", "reports:delete", "reports:export",
    "billing:view",
    "board:view", "board:edit",
  ],
  editor: [
    "company:view",
    "members:view",
    "reports:view", "reports:create", "reports:edit", "reports:export",
    "billing:view",
    "board:view", "board:edit",
  ],
  viewer: [
    "company:view",
    "members:view",
    "reports:view",
    "billing:view",
    "board:view",
  ],
};

// ═══════════════════════════════════════════════════════════════════
// Admin Roles Tab Component
// ═══════════════════════════════════════════════════════════════════

function AdminRolesTab() {
  const [modalRoleId, setModalRoleId] = useState<string | null | undefined>(undefined);

  const {
    data: rawData,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    refresh,
  } = useDataTable<AdminRole, { status: string }>({
    fetcher: (params) =>
      adminApi.listRoles({
        includeDeleted: params.status === "all" || undefined,
      }).then((res) => ({
        success: res.success,
        data: res.data
          ? {
              items: res.data,
              totalItems: res.data.length,
              totalPages: 1,
              page: 1,
              pageSize: res.data.length,
            }
          : undefined,
        error: res.error,
      })),
    pageSize: 100,
    initialFilters: { status: "active" },
  });

  const data = useMemo(() => {
    if (!search) return rawData;
    const q = search.toLowerCase();
    return rawData.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q)
    );
  }, [rawData, search]);

  const handleDelete = useCallback(
    async (role: AdminRole) => {
      if (!confirm(`Är du säker på att du vill ta bort rollen "${role.name}"?`)) return;
      try {
        const response = await adminApi.deleteRole(role.id);
        if (response.success) {
          refresh();
          toast.success("Rollen har tagits bort");
        }
      } catch {
        toast.error("Kunde inte ta bort rollen");
      }
    },
    [refresh]
  );

  const handleRestore = useCallback(
    async (role: AdminRole) => {
      try {
        const response = await adminApi.restoreRole(role.id);
        if (response.success) {
          refresh();
          toast.success("Rollen har återställts");
        }
      } catch {
        toast.error("Kunde inte återställa rollen");
      }
    },
    [refresh]
  );

  const columns: ColumnDef<AdminRole>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Namn",
        sortable: true,
        sortAccessor: (row) => row.name,
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök roller...",
        },
        cell: (row) => (
          <div className="flex items-center gap-2">
            <KeyRound size={14} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-900">{row.name}</span>
          </div>
        ),
      },
      {
        id: "description",
        header: "Beskrivning",
        hideBelow: "sm",
        cell: (row) => (
          <span className="text-sm text-gray-500 truncate max-w-xs block">
            {row.description || "-"}
          </span>
        ),
      },
      {
        id: "userCount",
        header: "Användare",
        sortable: true,
        sortAccessor: (row) => row.userCount,
        cell: (row) => (
          <span className="text-sm text-gray-600">{row.userCount}</span>
        ),
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => (row.deletedAt ? 1 : 0),
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: STATUS_OPTIONS,
        },
        cell: (row) =>
          row.deletedAt ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              Borttagen
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Aktiv
            </span>
          ),
      },
    ],
    [search, setSearch, filters.status, setFilter]
  );

  const rowActions: RowAction<AdminRole>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Redigera",
        icon: Edit,
        onClick: (row) => setModalRoleId(row.id),
      },
      {
        key: "restore",
        label: "Återställ",
        icon: RotateCcw,
        variant: "success",
        onClick: handleRestore,
        visible: (row) => !!row.deletedAt,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleDelete,
        visible: (row) => !row.deletedAt,
      },
    ],
    [handleDelete, handleRestore]
  );

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-600">
          Hantera systemroller och behörigheter för administration
        </p>
        <Button onClick={() => setModalRoleId(null)}>
          <Plus size={16} />
          Skapa ny roll
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        emptyMessage="Inga roller hittades"
        rowActions={rowActions}
        rowClassName={(row) => (row.deletedAt ? "opacity-50" : undefined)}
      />

      {modalRoleId !== undefined && (
        <AdminRoleModal
          roleId={modalRoleId}
          onClose={() => setModalRoleId(undefined)}
          onSaved={refresh}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Company Roles Tab Component
// ═══════════════════════════════════════════════════════════════════

function CompanyRolesTab() {
  const { data: statsData, isLoading } = useQuery({
    queryKey: ["admin", "company-role-stats"],
    queryFn: async () => {
      const response = await adminApi.getCompanyRoleStats();
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const stats = statsData as CompanyRoleStats | undefined;

  // Group permissions by category
  const permissionsByCategory = useMemo(() => {
    const categories = new Map<string, typeof COMPANY_PERMISSIONS[number][]>();
    for (const perm of COMPANY_PERMISSIONS) {
      const list = categories.get(perm.category) ?? [];
      list.push(perm);
      categories.set(perm.category, list);
    }
    return categories;
  }, []);

  const sortedRoles = useMemo(
    () => [...MEMBERSHIP_ROLES].sort((a, b) => ROLE_HIERARCHY[a] - ROLE_HIERARCHY[b]),
    []
  );

  const roleColors: Record<MembershipRole, string> = {
    owner: "bg-amber-100 text-amber-800",
    manager: "bg-blue-100 text-blue-800",
    editor: "bg-green-100 text-green-800",
    viewer: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="space-y-8">
      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Shield className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Om företagsroller</h3>
            <p className="text-sm text-blue-800">
              Företagsroller styr vad användare kan göra inom ett specifikt företag.
              Dessa är separata från admin-roller som ger systemåtkomst.
              Företagsroller är hårdkodade och kan inte ändras via admin.
            </p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Users className="text-primary-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {isLoading ? "..." : stats?.totalMemberships ?? 0}
              </p>
              <p className="text-sm text-gray-500">Totalt medlemskap</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {isLoading ? "..." : stats?.uniqueUsers ?? 0}
              </p>
              <p className="text-sm text-gray-500">Unika användare</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {isLoading ? "..." : stats?.uniqueCompanies ?? 0}
              </p>
              <p className="text-sm text-gray-500">Företag med medlemmar</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap gap-2">
            {sortedRoles.map((role) => (
              <div key={role} className="text-center">
                <p className="text-lg font-bold text-gray-900">
                  {isLoading ? "..." : stats?.byRole[role] ?? 0}
                </p>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${roleColors[role]}`}>
                  {ROLE_INFO[role].label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Role descriptions */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Rollbeskrivningar</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {sortedRoles.map((role) => (
            <div key={role} className="px-4 py-3 flex items-start gap-4">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[role]}`}>
                {ROLE_INFO[role].label}
              </span>
              <p className="text-sm text-gray-600 flex-1">{ROLE_INFO[role].description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Permission matrix */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Behörighetsmatris</h3>
          <p className="text-sm text-gray-500 mt-1">
            Visar vilka behörigheter varje roll har inom ett företag
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 sticky left-0 bg-gray-50">
                  Behörighet
                </th>
                {sortedRoles.map((role) => (
                  <th key={role} className="text-center px-4 py-3 text-sm font-medium text-gray-700 min-w-[100px]">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleColors[role]}`}>
                      {ROLE_INFO[role].label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Array.from(permissionsByCategory.entries()).map(([category, permissions]) => (
                <Fragment key={category}>
                  <tr className="bg-gray-50/50">
                    <td colSpan={sortedRoles.length + 1} className="px-4 py-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        {category}
                      </span>
                    </td>
                  </tr>
                  {permissions.map((perm) => (
                    <tr key={perm.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-700 sticky left-0 bg-white">
                        {perm.label}
                      </td>
                      {sortedRoles.map((role) => {
                        const hasPermission = ROLE_PERMISSIONS[role].includes(perm.id);
                        return (
                          <td key={role} className="text-center px-4 py-2">
                            {hasPermission ? (
                              <Check className="inline-block text-green-600" size={18} />
                            ) : (
                              <X className="inline-block text-gray-300" size={18} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════

export function AdminRoles() {
  const [activeTab, setActiveTab] = useState<TabId>("admin-roles");

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.roles.title} noIndex />
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Roller</h1>
          <p className="text-gray-600 mt-2">
            Hantera admin- och företagsroller
          </p>
        </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "admin-roles" && <AdminRolesTab />}
      {activeTab === "company-roles" && <CompanyRolesTab />}
    </div>
    </>
  );
}
